// src/client/renderer/character/head-tuning.ts
//
// Per-head visual tuning applied AFTER bbox-based bottom-alignment in head-swap.ts.
// Operator calibrates these values via debug/character-tune.html and writes them
// back to disk via the "Save to head-tuning.ts" button (POST /api/save-head-tuning).
// Defaults are no-op (scale=1, offsets=0, rotations=0) until tuned.

import type { HeadId } from './types';

export interface HeadTuning {
  /** Multiplied into headGroup.scale after bbox math. 1.0 = unit scale. */
  scale: number;
  /** Added to headGroup.position.x. 0 = centered on body. Positive = right. */
  xOffset: number;
  /** Added to headGroup.position.y after bbox math. 0 = bottom flush with body top. Positive = raise. */
  yOffset: number;
  /** Added to headGroup.position.z. 0 = centered on body. Positive = forward. */
  zOffset: number;
  /** Applied to headGroup.rotation.x (radians). 0 = no pitch. */
  rotationX: number;
  /** Applied to headGroup.rotation.y (radians). 0 = no yaw. */
  rotationY: number;
  /** Applied to headGroup.rotation.z (radians). 0 = no roll. */
  rotationZ: number;
}

export const HEAD_TUNING: Record<HeadId, HeadTuning> = {
  horse: {
    scale: 1.1449999998392875,
    xOffset: 0.02388649329694103,
    yOffset: -0.38162761607653173,
    zOffset: 0.08055010315447136,
    rotationX: -0.4938031711889127,
    rotationY: 0.720681578519865,
    rotationZ: 0.02553658471215915,
  },
  chicken: {
    scale: 0.9250000000007623,
    xOffset: 0.01922848466454219,
    yOffset: -0.053737728981285704,
    zOffset: 0.05784645499868762,
    rotationX: -0.003168140699419132,
    rotationY: 0.47616385632510044,
    rotationZ: 0.004990930974586834,
  },
  'cardboard-box': {
    scale: 1.06,
    xOffset: 0.0,
    yOffset: -0.16699964500604425,
    zOffset: -0.01811379328429258,
    rotationX: 0.0,
    rotationY: 0.0,
    rotationZ: 0.0,
  },
  shark: {
    scale: 1.1,
    xOffset: 0.07157609505369361,
    yOffset: -0.15053632954117546,
    zOffset: 0.14810506136555573,
    rotationX: -0.012601961075167785,
    rotationY: 0.5618800110743677,
    rotationZ: 0.026372449913575617,
  },
  pumpkin: {
    scale: 1.14,
    xOffset: 0.0,
    yOffset: -0.13220261131655264,
    zOffset: 0.0,
    rotationX: 0.0,
    rotationY: 0.09143823123229795,
    rotationZ: 0.0,
  },
  alien: {
    scale: 1.1200000000035821,
    xOffset: 0.04000000000000001,
    yOffset: -0.15500000041350923,
    zOffset: 0.025,
    rotationX: -0.03737996458259015,
    rotationY: 0.5049460950514898,
    rotationZ: 0.07787403223001586,
  },
  unicorn: {
    scale: 1.175,
    xOffset: 0.05180037312590018,
    yOffset: -0.08000000002983258,
    zOffset: 0.06856483772499339,
    rotationX: -0.007780902747810838,
    rotationY: 0.5880070872853163,
    rotationZ: 0.01446494563621409,
  },
  'traffic-cone': {
    scale: 1.03,
    xOffset: 0.0,
    yOffset: -0.049020783504132304,
    zOffset: 0.0,
    rotationX: 0.0,
    rotationY: 0.0,
    rotationZ: 0.0,
  },
};
